// apps/web/src/features/russia-requests/components/editor/russiaPositions.model.ts
//
// Pure (no-React) row model + body builders for the Russia SIMPLE position
// editor (#1545). Extracted from useRussiaDraftPositions.ts so the hook
// orchestrates state while these stay framework-free and unit-addressable.

import type {
  CreateRussiaRequestItemInput,
  PatchRussiaRequestItemInput,
  RussiaRequest,
} from '@magimp/shared';

/** Editable per-position scalar fields. `name` required; rest tolerant. */
export interface RussiaPositionFields {
  name: string;
  brand: string;
  material: string;
  description: string;
  weightKg: number | null;
  volumeM3: number | null;
  quantity: number;
  productQuantity: number | null;
}

export interface RussiaPositionRow extends RussiaPositionFields {
  /** Stable client id for React keys + cross-render addressing. */
  localId: string;
  /** Server UUID once the create POST has resolved; null while pending. */
  serverId: string | null;
  photos: string[];
  uploading: boolean;
  uploadError: string | null;
}

export const PATCH_DEBOUNCE_MS = 600;

export function emptyRow(localId: string): RussiaPositionRow {
  return {
    localId,
    serverId: null,
    name: '',
    brand: '',
    material: '',
    description: '',
    weightKg: null,
    volumeM3: null,
    quantity: 1,
    productQuantity: null,
    photos: [],
    uploading: false,
    uploadError: null,
  };
}

/**
 * @aiPurpose Seed already-persisted SIMPLE positions for edit mode (#1571).
 *   Maps each `RussiaRequestItem` to a row carrying its server identity
 *   (`serverId = item.id`) and existing photos, sorted by `sortOrder`.
 * @aiInvariant The returned rows are ALREADY-PERSISTED: their photo keys are
 *   put on the row state ONLY. They must NEVER flow through the photo-upload
 *   path (`addPhoto`) and therefore never enter the `uploadedKeysRef`
 *   never-saved accumulator — otherwise a confirmed discard-close would GC
 *   real, referenced photos (#1561 cleanup). Seeding the row directly is the
 *   guarantee that existing keys stay out of that accumulator.
 */
export function seededPositionsFromRequest(request: RussiaRequest): RussiaPositionRow[] {
  return [...request.items]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((item) => ({
      localId: crypto.randomUUID(),
      serverId: item.id,
      name: item.name,
      brand: item.brand,
      material: item.material ?? '',
      description: item.description,
      weightKg: item.weightKg,
      volumeM3: item.volumeM3,
      quantity: item.quantity,
      productQuantity: item.productQuantity,
      photos: [...item.photos],
      uploading: false,
      uploadError: null,
    }));
}

export function createInputFromFields(fields: RussiaPositionFields): CreateRussiaRequestItemInput {
  return {
    name: fields.name,
    brand: fields.brand,
    material: fields.material,
    description: fields.description,
    weightKg: fields.weightKg,
    volumeM3: fields.volumeM3,
    quantity: fields.quantity,
    productQuantity: fields.productQuantity,
    photos: [],
  };
}

// Shared PATCH body: the full live field set the create body never carries
//   (photos) plus the latest scalars. One builder so flushPatch and both
//   flushPending branches write byte-identical bodies.
export function buildPatchBody(row: RussiaPositionRow): PatchRussiaRequestItemInput {
  return {
    name: row.name,
    brand: row.brand,
    material: row.material,
    description: row.description,
    weightKg: row.weightKg,
    volumeM3: row.volumeM3,
    quantity: row.quantity,
    productQuantity: row.productQuantity,
    photos: row.photos,
  };
}
